import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import Dashboard from './components/dashboard/Dashboard';
import EntitiesPage from './pages/EntitiesPage';
import AssetsPage from './pages/AssetsPage';
import LiabilitiesPage from './pages/LiabilitiesPage';
import ExitEventsPage from './pages/ExitEventsPage';
import ImportWizardPage from './pages/ImportWizardPage';
import AddClientPage from './pages/AddClientPage';
import {
  LiquidityRunwayPage,
  EstateTaxPage,
} from './pages/ModulePages';
import CapitalStructurePage from './pages/capital-structure';
import StrategyWorkbenchPage from './pages/strategy-workbench';
import GiftTrackerPage from './pages/gift-tracker';
import EntityModelerPage from './pages/entity-modeler';
import ReportsPage from './pages/ReportsPage';
import OnboardingWizard from './pages/onboarding';
import TimingOverlayPage from './pages/timing-overlay';
import FamilyMembersPage from './pages/FamilyMembersPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen bg-ivory-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-xl text-navy-700 mb-2">Osprey Legacy</h2>
          <p className="text-sm text-navy-400 font-sans animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Guard: requires at least one client and an active selection
function RequireClient({ children }: { children: React.ReactNode }) {
  const { user, activeClient } = useAuth();

  if (!user?.clients?.length || !activeClient) {
    return <Navigate to="/add-client" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />} />

      {/* Add client — standalone page (full-screen for first client, simple form for subsequent) */}
      <Route path="/add-client" element={
        <ProtectedRoute><AddClientPage /></ProtectedRoute>
      } />

      <Route element={<ProtectedRoute><RequireClient><AppLayout /></RequireClient></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="liquidity" element={<LiquidityRunwayPage />} />
        <Route path="capital" element={<CapitalStructurePage />} />
        <Route path="estate-tax" element={<EstateTaxPage />} />
        <Route path="strategy" element={<StrategyWorkbenchPage />} />
        <Route path="gifts" element={<GiftTrackerPage />} />
        <Route path="entities" element={<EntityModelerPage />} />
        <Route path="timing" element={<TimingOverlayPage />} />

        {/* Data management */}
        <Route path="entities-mgmt" element={<EntitiesPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="liabilities-mgmt" element={<LiabilitiesPage />} />
        <Route path="exit-events" element={<ExitEventsPage />} />
        <Route path="import" element={<ImportWizardPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="members" element={<FamilyMembersPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
